import { Component, OnDestroy, OnInit } from "@angular/core";
import { SpinnerComponent } from "../../shared/icons/spinner/spinner.component";
import { Router } from "@angular/router";
import { RedirectService } from "../../services/redirect.service";
import { VersionService } from "../../services/version.service";
import { TranslatePipe } from "@ngx-translate/core";
import { CommonModule } from "@angular/common";

@Component({
  selector: "app-continue",
  standalone: true,
  imports: [SpinnerComponent, TranslatePipe, CommonModule],
  templateUrl: "./continue.component.html",
  styleUrl: "./continue.component.css",
})
export class ContinueComponent implements OnInit, OnDestroy {
  public showRedirectMessage = false;

  private messageTimer: ReturnType<typeof setTimeout> | null = null;
  private fallbackTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private router: Router,
    private redirectService: RedirectService,
    private versionService: VersionService,
  ) { }

  public ngOnInit(): void {
    console.info("ContinueComponent.ngOnInit - URL:", window.location.href);
    const searchParams = new URLSearchParams(window.location.search);

    const oidcStep = searchParams.get("oidcStep");
    if (oidcStep === "error" || oidcStep === "consent") {
      console.warn(`ContinueComponent.ngOnInit - Gigya redirected to the OIDC "${oidcStep}" page - triggering fallback. full search:`, window.location.search);
      this.versionService.redirectToFallback();
      return;
    }
    console.info("ContinueComponent.ngOnInit - no oidcStep error/consent branch taken. oidcStep:", oidcStep);

    let redirectTo = searchParams.get("redirect_to");
    if (redirectTo) {
      console.info("ContinueComponent.ngOnInit - redirect_to param present, attempting redirect to:", redirectTo);
      const redirected = this.redirectService.redirect(redirectTo);
      if (!redirected) {
        console.warn("ContinueComponent.ngOnInit - redirect_to was blocked (disallowed domain) - staying on /continue with no further action taken. target was:", redirectTo);
      }
      return;
    }

    if (!searchParams.get("oidcEnabled") && !this.versionService.nativeAppOidc) {
      console.info("ContinueComponent.ngOnInit - oidcEnabled param missing, reloading page with oidcEnabled=true.");
      return this.enableOidc();
    }
    if (this.versionService.nativeAppOidc) {
      console.info("ContinueComponent.ngOnInit - native app OIDC client, gigya.oidc.js already loaded at bootstrap - skipping the oidcEnabled reload.");
    }

    console.info('ContinueComponent.ngOnInit - oidcEnabled present, waiting for automatic redirection by Gigya. Arming 2s message timer and 5s fallback timer.');
    this.messageTimer = setTimeout(() => {
      console.info('ContinueComponent - 2s elapsed with no redirect from Gigya yet, showing "redirecting" message to user.');
      this.showRedirectMessage = true;
    }, 2000);

    this.fallbackTimer = setTimeout(() => {
      console.warn('ContinueComponent - Gigya did NOT redirect within 5s - triggering fallback safety net. current URL:', window.location.href);
      this.versionService.redirectToFallback();
    }, 5000);
  }

  public ngOnDestroy(): void {
    if (this.messageTimer) {
      console.info("ContinueComponent.ngOnDestroy - clearing pending message timer.");
      clearTimeout(this.messageTimer);
    }
    if (this.fallbackTimer) {
      console.info("ContinueComponent.ngOnDestroy - clearing pending fallback timer (component destroyed before the 5s fallback fired - likely means Gigya's own redirect or navigation happened first).");
      clearTimeout(this.fallbackTimer);
    }
  }

  private enableOidc() {
    // we enable oidc by reloading the page with the oidcEnabled param
    const url = new URL(window.location.href);
    url.searchParams.set("oidcEnabled", "true");
    console.info("ContinueComponent.enableOidc - reloading to:", url.toString());
    window.location.href = url.toString();
  }
}
